class TradeApplicationModal extends HTMLElement {
  constructor() {
    super();

    this.application_page = this.dataset.applicationPage || "/pages/trade-application-form";
    this.application_script = this.dataset.applicationScript;
    this.body = this.querySelector("[data-trade-application-modal-body]");
    this.is_open = false;
    this.is_loaded = false;
    this.load_promise = null;
    this.section_id = null;
    this.section_html = null;
    this.previous_active_element = null;
    this.events = [
      {
        el: document,
        type: "click",
        callback: this.outsideClickHandler.bind(this),
      },
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: document,
        type: "keydown",
        callback: this.keyboardHandler.bind(this),
      },
    ];
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  async outsideClickHandler(event) {
    const trigger = event.target.closest("[data-trade-application-modal-trigger]");
    if (!trigger) return;

    event.preventDefault();
    this.open(trigger);
  }

  clickHandler(event) {
    const is_close = event.target.closest("[data-trade-application-modal-close]");
    if (is_close) {
      event.preventDefault();
      this.close();
    }
  }

  keyboardHandler(event) {
    if (!this.is_open || event.key !== "Escape") return;

    this.close();
  }

  async open(trigger) {
    this.is_open = true;
    this.previous_active_element = trigger || document.activeElement;
    this.setAttribute("aria-hidden", "false");
    document.documentElement.style.overflow = "hidden";

    try {
      await this.loadApplication();
      if (this.is_open) {
        this.querySelector("[data-trade-application-modal-close]")?.focus();
      }
    } catch (error) {
      console.error("Unable to load trade application modal", error);
      window.location.href = trigger?.href || this.application_page;
    }
  }

  close() {
    this.is_open = false;
    this.setAttribute("aria-hidden", "true");
    document.documentElement.style.overflow = "";
    this.previous_active_element?.focus?.();
  }

  async loadApplication() {
    if (this.is_loaded) return;
    if (this.load_promise) return this.load_promise;

    this.load_promise = this.fetchAndRenderApplication();

    try {
      await this.load_promise;
      this.is_loaded = true;
    } finally {
      this.load_promise = null;
    }
  }

  async fetchAndRenderApplication() {
    this.body.innerHTML =
      '<div class="trade-application-modal__status">Loading application form...</div>';

    if (!this.section_id) {
      this.section_id = await this.fetchApplicationSectionId();
    }

    const section_url = this.buildUrl(this.application_page, { section_id: this.section_id });
    const response = await fetch(section_url);
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);

    this.section_html = await response.text();
    this.body.innerHTML = this.section_html;
    this.syncApplicationColors();
    await this.ensureApplicationScript();
  }

  syncApplicationColors() {
    const application_module = this.body.querySelector("application-module");
    if (!application_module) return;

    const computed_styles = window.getComputedStyle(application_module);
    ["--application-background", "--theme-accent", "--theme-accent-medium", "--theme-accent-light"].forEach(
      (property) => {
        const value = computed_styles.getPropertyValue(property).trim();
        if (value) {
          this.style.setProperty(property, value);
        }
      },
    );
  }

  async fetchApplicationSectionId() {
    const response = await fetch(this.application_page);
    if (!response.ok) throw new Error(`Application page request failed: ${response.status}`);

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const application_module = doc.querySelector("application-module");
    const section = application_module?.closest("[id^='shopify-section-']");
    const section_id = section?.id?.replace("shopify-section-", "");

    if (!section_id) throw new Error("Application section ID was not found");

    return section_id;
  }

  buildUrl(path, params) {
    const url = new URL(path, window.location.origin);
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
    return url.toString();
  }

  ensureApplicationScript() {
    if (!this.application_script || customElements.get("application-module")) {
      return Promise.resolve();
    }

    const existing_script = document.head.querySelector(
      `script[data-trade-application-modal-script][src="${this.application_script}"]`,
    );
    if (existing_script) {
      return new Promise((resolve, reject) => {
        existing_script.addEventListener("load", resolve, { once: true });
        existing_script.addEventListener("error", reject, { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = this.application_script;
      script.defer = true;
      script.setAttribute("data-trade-application-modal-script", "true");
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
}

if (!customElements.get("trade-application-modal")) {
  customElements.define("trade-application-modal", TradeApplicationModal);
}
